/**
 * Director Import Excel Template Generator
 */

export const DIRECTOR_TEMPLATE_COLUMNS = [
  { key: 'firstName', label: 'First Name', required: true, example: 'John' },
  { key: 'lastName', label: 'Last Name', required: true, example: 'Smith' },
  { key: 'companyName', label: 'Company Name', required: true, example: 'Etisalat Group PJSC' },
  { key: 'dateOfBirth', label: 'Date of Birth', required: false, example: '1980-05-15', format: 'YYYY-MM-DD' },
  { key: 'email', label: 'Email', required: false, example: 'john.smith@example.com' },
  { key: 'phone', label: 'Phone', required: false, example: '+971501234567' },
  { key: 'nationality', label: 'Nationality', required: false, example: 'UAE' },
  { key: 'passportNumber', label: 'Passport Number', required: false, example: 'P1234567' },
  { key: 'emiratesId', label: 'Emirates ID', required: false, example: '784-1234-1234567-1' },
  { key: 'gender', label: 'Gender', required: false, example: 'Male' },
  { key: 'position', label: 'Position', required: false, example: 'Director' },
];

/**
 * Generate Excel template data
 */
export function generateTemplateData() {
  const headers = DIRECTOR_TEMPLATE_COLUMNS.map(col => col.label);
  const examples = DIRECTOR_TEMPLATE_COLUMNS.map(col => col.example);
  const notes = DIRECTOR_TEMPLATE_COLUMNS.map(col => 
    col.required ? `Required${col.format ? ` (${col.format})` : ''}` : `Optional${col.format ? ` (${col.format})` : ''}`
  );
  
  return {
    headers,
    examples,
    notes,
    columns: DIRECTOR_TEMPLATE_COLUMNS,
  };
}

/**
 * Download Excel template
 */
export function downloadDirectorTemplate() {
  const { headers, examples, notes } = generateTemplateData();
  
  // Create CSV content (simpler than XLSX, widely compatible)
  const csvContent = [
    headers.join(','),
    examples.join(','),
    `"${notes.join('","')}"`,
  ].join('\n');
  
  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `director_import_template_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Format date to YYYY-MM-DD
 * Handles Excel dates (serial numbers or Date objects) and string dates
 */
function formatDateForBackend(value) {
  if (!value) return '';
  
  // If it's already a string in YYYY-MM-DD format, return it
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    // Try to parse other string formats
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    return '';
  }
  
  // If it's a Date object
  if (value instanceof Date) {
    if (!isNaN(value.getTime())) {
      return value.toISOString().split('T')[0];
    }
    return '';
  }
  
  // If it's an Excel serial number (number of days since 1900-01-01)
  if (typeof value === 'number') {
    const excelEpoch = new Date(1900, 0, 1);
    const date = new Date(excelEpoch.getTime() + (value - 2) * 24 * 60 * 60 * 1000);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    return '';
  }
  
  return '';
}

/**
 * Parse imported row
 */
export function parseImportedRow(row, index) {
  // Helper to safely get and trim string values
  const safeString = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return String(value).trim();
  };
  
  // Helper to safely get company name - also check old "Company ID" column for backward compatibility
  const getCompanyName = (row) => {
    return safeString(row['Company Name'] || row.companyName || row['Company ID'] || row.companyId);
  };
  
  const parsed = {
    _rowIndex: index,
    firstName: safeString(row['First Name'] || row.firstName),
    lastName: safeString(row['Last Name'] || row.lastName),
    companyName: getCompanyName(row),
    dateOfBirth: formatDateForBackend(row['Date of Birth'] || row.dateOfBirth),
    email: safeString(row['Email'] || row.email).toLowerCase(),
    phone: safeString(row['Phone'] || row.phone),
    nationality: safeString(row['Nationality'] || row.nationality),
    passportNumber: safeString(row['Passport Number'] || row.passportNumber),
    emiratesId: safeString(row['Emirates ID'] || row.emiratesId),
    gender: safeString(row['Gender'] || row.gender),
    position: safeString(row['Position'] || row.position),
  };
  
  return parsed;
}

/**
 * Validate imported row
 */
export function validateImportedRow(row) {
  const errors = [];
  
  // Required fields
  if (!row.firstName) {
    errors.push('First Name is required');
  }
  if (!row.lastName) {
    errors.push('Last Name is required');
  }
  if (!row.companyName || row.companyName === '') {
    errors.push('Company Name is required');
  }
  
  // Email format validation
  if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
    errors.push('Invalid email format');
  }
  
  // Date of birth format validation
  if (row.dateOfBirth) {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(row.dateOfBirth)) {
      errors.push('Date of Birth must be in YYYY-MM-DD format');
    } else {
      const date = new Date(row.dateOfBirth);
      if (isNaN(date.getTime())) {
        errors.push('Invalid Date of Birth');
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Calculate similarity score between two directors
 * Returns a score between 0 and 100
 */
export function calculateSimilarityScore(importedDirector, existingDirector) {
  let score = 0;
  let maxScore = 0;
  
  // Exact ID matches (highest weight)
  if (importedDirector.passportNumber && existingDirector.passportNumber) {
    maxScore += 30;
    if (importedDirector.passportNumber.toLowerCase() === existingDirector.passportNumber.toLowerCase()) {
      score += 30;
    }
  }
  
  if (importedDirector.emiratesId && existingDirector.emiratesId) {
    maxScore += 30;
    if (importedDirector.emiratesId.toLowerCase() === existingDirector.emiratesId.toLowerCase()) {
      score += 30;
    }
  }
  
  // Email match (high weight)
  if (importedDirector.email && existingDirector.email) {
    maxScore += 25;
    if (importedDirector.email.toLowerCase() === existingDirector.email.toLowerCase()) {
      score += 25;
    }
  }
  
  // Name match (moderate weight)
  if (importedDirector.firstName && existingDirector.firstName) {
    maxScore += 10;
    if (importedDirector.firstName.toLowerCase() === existingDirector.firstName.toLowerCase()) {
      score += 10;
    } else if (importedDirector.firstName.toLowerCase().includes(existingDirector.firstName.toLowerCase()) ||
               existingDirector.firstName.toLowerCase().includes(importedDirector.firstName.toLowerCase())) {
      score += 5; // Partial match
    }
  }
  
  if (importedDirector.lastName && existingDirector.lastName) {
    maxScore += 10;
    if (importedDirector.lastName.toLowerCase() === existingDirector.lastName.toLowerCase()) {
      score += 10;
    } else if (importedDirector.lastName.toLowerCase().includes(existingDirector.lastName.toLowerCase()) ||
               existingDirector.lastName.toLowerCase().includes(importedDirector.lastName.toLowerCase())) {
      score += 5; // Partial match
    }
  }
  
  // Date of birth match (moderate weight)
  if (importedDirector.dateOfBirth && existingDirector.dateOfBirth) {
    maxScore += 15;
    const importedDate = new Date(importedDirector.dateOfBirth).toISOString().split('T')[0];
    const existingDate = new Date(existingDirector.dateOfBirth).toISOString().split('T')[0];
    if (importedDate === existingDate) {
      score += 15;
    }
  }
  
  // Calculate percentage
  if (maxScore === 0) return 0;
  return Math.round((score / maxScore) * 100);
}

/**
 * Determine match confidence level
 */
export function getMatchConfidenceLevel(score) {
  if (score >= 90) return { level: 'exact', label: 'Exact Match', color: 'error' };
  if (score >= 70) return { level: 'high', label: 'High Confidence', color: 'warning' };
  if (score >= 50) return { level: 'medium', label: 'Possible Match', color: 'info' };
  return { level: 'low', label: 'Low Confidence', color: 'default' };
}
